// V70.195 — Registry izin anahtarı → permission kataloğu çevirisi (frontend tarafı).
//
// Çeviri tablosu `hrSiteRegistry.json` içindeki `permission_alias` bloğundadır ve
// backend `config/hrRegistryPermissionMap.js` AYNI bloğu okur. Tabloyu iki tarafa
// kopyalamak spec §2.6'yı ("Site/Route Registry tek source of truth") ihlal ederdi;
// bu dosya yalnız okuma yardımcısıdır, kendi listesi YOKTUR.
//
// Neden gerekli: registry'nin izin sözlüğü motorunkinden daha ince taneli
// (`employee.identity.read`), motor ise resource+action+field_group ile çalışıyor.
// Menü ve rol editörü "bu kişi bu sayfayı görebilir mi" sorusunu ancak çeviriyle
// sorabilir.

import REGISTRY from './hrSiteRegistry.json';

const ALIAS = REGISTRY.permission_alias || {};
const ACTION_ALIAS = ALIAS.actions || {};
const RESOURCE_ALIAS = ALIAS.resources || {};

/**
 * `employee.identity.read` → `{ resource: 'employee.profile', action: 'view',
 *                               field: 'identity_sensitive' }`
 * Çözülemezse `null` — çağıran fail-closed davranmalı.
 */
export const decodePermissionKey = (key) => {
  if (!key || typeof key !== 'string') return null;
  const i = key.lastIndexOf('.');
  if (i <= 0) return null;

  const rawResource = key.slice(0, i);
  const rawAction = key.slice(i + 1);

  const action = ACTION_ALIAS[rawAction] || rawAction;
  const alias = RESOURCE_ALIAS[rawResource];
  const resource = alias ? alias.resource : rawResource;

  return { resource, action, field: (alias && alias.field) || null, raw: key };
};

/**
 * Bir registry node'unun MENÜDE görünmesi için gereken yetki.
 * Sırayla `menu_view` → `list` → `read` denenir; hiçbiri yoksa null döner
 * (o zaman menü kararı legacy anahtara kalır).
 */
export const menuPermissionOf = (node) => {
  const perms = (node && node.permissions) || {};
  const key = perms.menu_view || perms.list || perms.read;
  return key ? decodePermissionKey(key) : null;
};

/**
 * Efektif yetki listesinden (backend `/hr/permissions/effective`) hızlı sorgu kümesi.
 * `rows`: [{ resource, action, allow, scopes, field_groups }]
 */
// V71 — menü için yalnız AÇIK atamalar sayılır (rol/set/vekalet/istisna). builtin (herkese
// verilen self/rehber/referans) ve legacy köprü grant'leri menüyü açmaz; legacy zaten
// AuthContext.hasPermission üzerinden ayrıca değerlendirilir.
const EXPLICIT_SOURCE = /^(role:|delegation:|override)/;

// V71.4.1 — Çalışan (EMPLOYEE) herkesin temel rolü: şirket geneli tanım/rehber okuması talep
// formları içindir (köprü açıkken builtin:reference ile aynı). Bu rolden gelen geniş kapsam
// Yönetim/Operasyon ekranı açmaz; sunucu yetkisi değişmez (hrRoleSeed EMPLOYEE).
export const isWideMenuSource = (s) => EXPLICIT_SOURCE.test(String(s)) && String(s) !== 'role:EMPLOYEE';

export const buildPermissionIndex = (rows) => {
  const allow = new Set();
  const explicit = new Set();
  const explicitWide = new Set(); // açık atama + kapsam 'self'ten geniş (yönetim/operasyon ekranları)
  const fields = new Map();
  for (const r of rows || []) {
    if (!r.allow) continue;
    const ra = `${r.resource}:${r.action}`;
    allow.add(ra);
    if ((r.sources || []).some((s) => EXPLICIT_SOURCE.test(String(s)))) {
      explicit.add(ra);
      if ((r.sources || []).some(isWideMenuSource)
        && (r.scopes || []).some((s) => s !== 'self' && s !== 'none')) explicitWide.add(ra);
    }
    if (r.field_groups && r.field_groups.length) {
      const key = r.resource;
      const cur = fields.get(key) || new Set();
      r.field_groups.forEach((g) => cur.add(g));
      fields.set(key, cur);
    }
  }
  return {
    can: (resource, action) => allow.has(`${resource}:${action}`),
    canExplicit: (resource, action, wide) => (wide ? explicitWide : explicit).has(`${resource}:${action}`),
    hasField: (resource, group) => {
      const s = fields.get(resource);
      return !!(s && s.has(group));
    },
    size: allow.size,
  };
};
